import axios, { AxiosRequestConfig } from 'axios-0-27-2'
import { useMeStore } from '~/modules/SDK/me/useMeStore'
import { fr_agents } from '~/pages/heineken_template/_fr/fr_agents'
import { fr_me } from '~/pages/heineken_template/_fr/fr_me'
import { __PRODUCTION__ } from '~/utils/__PRODUCTION__'

/**
 * @example
 *   !! 更多使用情境
 *   !! 請參考 testing
 *
 * @example
 *   //
 *   // 如果你有要區分 prod 和 beta 的一級網域
 *
 *   new Urlu({
 *     prod: 'https://api.futures-ai.com/v1/prod',
 *     beta: 'https://api.futures-ai.com/v1/beta',
 *     alpha: 'https://api.futures-ai.com/v1/alpha',
 *   })
 *
 * @example
 *   //
 *   // 如果你只是要拿 baseUrl 來用
 *   const tradeApi = new Urlu({...})
 *
 *   log(tradeApi.baseUrl)
 *   //
 *   // 或
 *   log(`${tradeApi}/v1/api/kbars`)
 */
export class Urlu {
  baseUrlrc!: {
    /** 線上客戶用版 */
    prod: string
    /** 前端開發用版、QA測試用版 */
    beta?: string
    /** 後端測試用版 */
    alpha?: string
  }

  baseUrlUsedEnv!: keyof typeof Urlu.prototype.baseUrlrc

  constructor(baseUrlOrOptions: ParametersHead<Urlu['changeBaseUrl']>) {
    this.changeBaseUrl(baseUrlOrOptions)
  }

  get baseUrl() {
    return this.baseUrlrc[this.baseUrlUsedEnv] || ''
  }

  changeBaseUrl(
    baseUrlOrOptions:
      | string
      | {
          /** E.g. `'https://cats.com'` */
          baseUrl: typeof Urlu.prototype.baseUrlrc
        },
  ) {
    this.baseUrlrc =
      typeof baseUrlOrOptions === 'string'
        ? {
            prod: baseUrlOrOptions,
            beta: baseUrlOrOptions,
            alpha: baseUrlOrOptions,
          }
        : baseUrlOrOptions.baseUrl

    if (!this.baseUrlrc.beta) {
      this.baseUrlrc.beta = this.baseUrlrc.prod
    }
    if (!this.baseUrlrc.alpha) {
      this.baseUrlrc.alpha = this.baseUrlrc.beta
    }

    this.baseUrlUsedEnv = __PRODUCTION__ ? 'prod' : 'beta'
  }

  /**
   * @example
   *   //
   *   // 一般使用，正常用就好（內建含 jwt）
   *   this.request.axios.get()
   *
   *   //
   *   // 如果要額外帶 agent 和 product（內建含 jwt）
   *   this.request.withAgent.withProduct.axios.get()
   *
   *   //
   *   // 如果你特別不需要 jwt 或者是有 jwt 會報錯
   *   // 例如打去 https://storage.googleapis.com
   *   // 就不行有 jwt=undefined
   *   this.request.removeJwt.axios.get()
   *
   * @example
   *   //
   *   // 預設某個 APIs 底下的 async function 通通自帶 agentName
   *   export class TvAPIs extends Urlu {
   *     wsUrl = ''
   *
   *     get request() {
   *       // 從這裡這樣來覆寫父類
   *       return super.request.withAgent.withProduct
   *     }
   *
   *     async fetchHistory() {
   *       this.request.axios.get() // 已自帶 agentName（內建含 jwt）
   *     }
   *   }
   */
  get request() {
    const config: AxiosRequestConfig = {
      baseURL: this.baseUrl,
      headers: {
        get Authorization() {
          const jwt = fr_me.jwt || useMeStore.getState().meFirebaseState?.jwt

          return `Bearer ${jwt}`
        },
      },
    }

    return {
      get axios() {
        return axios.create(config)
      },

      /** - 如果你 `jwt=undefined` 會報錯 */
      get removeJwt() {
        if (!config.headers) return this

        delete config.headers.Authorization

        return this
      },

      /**
       * - 通常只有某些特定 API 需要（by Yucheng）
       * - 附加發送 `?agent=${agent}` 上
       */
      get withAgent() {
        config.params = {
          ...config.params,
          agent: fr_agents.agent,
        }
        return this
      },

      /**
       * - 通常只有某些特定 API 需要（by Yucheng）
       * - 附加發送 `?product=${product}` 上
       */
      get withProduct() {
        config.params = {
          ...config.params,
          product: fr_agents.product,
        }
        return this
      },
    }
  }

  /** 返回 this.baseUrl */
  toString() {
    return this.baseUrl
  }
}
